import { useState, useEffect, useCallback } from 'react';
import type {
  PricingSaasData,
  PricingSaasError,
  PricingSaasResponse,
  UsePricingSaasDataReturn
} from '../types';

export interface PricingVersion {
  version: string;
  quarter?: string;
  month?: string;
  week?: string;
}

export interface UsePricingSaasDataReturnWithVersions extends UsePricingSaasDataReturn {
  versions: PricingVersion[];
  selectedVersion: string | null;
  setSelectedVersion: (version: string | null) => void;
  versionsLoading: boolean;
}

export const usePricingSaasData = (slug: string): UsePricingSaasDataReturnWithVersions => {
  const [data, setData] = useState<PricingSaasData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<PricingSaasError | null>(null);
  const [selectedPageIndex, setSelectedPageIndex] = useState<number>(0);

  // Version state
  const [versions, setVersions] = useState<PricingVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [versionsLoading, setVersionsLoading] = useState<boolean>(false);

  // Fetch available versions - now handled in fetchData since API returns both
  const fetchVersions = useCallback(async () => {
    // Versions are now fetched as part of the main data call
    // This function is kept for compatibility but does nothing
    // The versions will be populated when fetchData is called
  }, [slug]);

  // Fetch pricing data (optionally for a specific version)
  const fetchData = useCallback(async (version?: string | null) => {
    if (!slug) {
      setError({
        type: 'PARSE_ERROR',
        message: 'Company slug is required'
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const API_BASE = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL ||
                       'https://nyqqynbtcrqdmhezmhsp.supabase.co/functions/v1/management';
      const API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY;

      // Use the slug as-is (don't add .pricing suffix)
      const compositeSlug = slug;

      // Build URL with optional version parameter
      const url = version
        ? `${API_BASE}/pricing/${compositeSlug}?version=${version}`
        : `${API_BASE}/pricing/${compositeSlug}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'x-api-key': API_KEY,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const status = response.status;
        let errorType: PricingSaasError['type'] = 'UNKNOWN';
        let errorMessage = `API error: ${status} ${response.statusText}`;

        // Try to parse error response body for more context
        let errorBody: any = null;
        try {
          errorBody = await response.json();
        } catch (e) {
          // If we can't parse the body, continue with status-based classification
        }

        if (status === 404) {
          errorType = 'NOT_FOUND';
          errorMessage = 'Pricing data not found for this company';
        } else if (status === 401 || status === 403) {
          errorType = 'AUTH_ERROR';
          errorMessage = 'Authentication failed';
        } else if (status >= 500) {
          // Check if it's a "NO_DATA" error (should be treated as NOT_FOUND, not server error)
          if (errorBody?.code === 'NO_DATA') {
            errorType = 'NOT_FOUND';
            errorMessage = errorBody.error || errorBody.details?.message || 'No pricing data available';
          } else {
            errorType = 'NETWORK_ERROR';
            errorMessage = 'Server error occurred';
          }
        }

        setError({
          type: errorType,
          message: errorMessage,
          status
        });
        setData(null);
        return;
      }

      const responseData = await response.json();

      if (responseData && responseData.plans) {
        // Extract and update versions if available
        if (responseData.available_versions && Array.isArray(responseData.available_versions)) {
          setVersions(responseData.available_versions);
        }

        // Map the API response to our internal format
        const pricingSaasResponse: PricingSaasResponse = {
          slug: responseData.slug,
          company_slug: responseData.company_slug,
          version: responseData.version,
          page_id: responseData.page_id,
          scrape: responseData.scrape ? {
            timestamp: responseData.scrape.created_at,
            url: responseData.scrape.page_url
          } : undefined,
          plans: responseData.plans.map((plan: any) => ({
            name: plan.internal_name || plan.display_name,
            display_name: plan.display_name,
            plan_name: plan.display_name,
            add_on: plan.add_on || false,
            charges: plan.charges.map((charge: any) => ({
              billing: charge.billing,
              currency: charge.currency,
              price: charge.price,
              charge_type: charge.charge_type,
              usage_type: charge.usage_type,
              included_quantity: charge.included_quantity,
              overage_info: charge.overage_info,
              pricing_metric: charge.pricing_metric ? {
                name: charge.pricing_metric.display_name,
                display_name: charge.pricing_metric.display_name,
                modality: charge.pricing_metric.modality
              } : undefined
            })),
            discounts: plan.discounts?.map((discount: any) => ({
              type: discount.discount_category,
              amount: discount.amount,
              amount_type: discount.amount_type,
              description: discount.description,
              discount_category: discount.discount_category,
              duration: discount.duration
            })) || [],
            thresholds: plan.thresholds?.map((threshold: any) => ({
              id: threshold.id,
              plan_id: threshold.plan_id,
              pricing_metric_id: threshold.pricing_metric_id,
              quantity: threshold.quantity,
              pricing_metric: threshold.pricing_metric ? {
                name: threshold.pricing_metric.display_name,
                display_name: threshold.pricing_metric.display_name,
                modality: threshold.pricing_metric.modality
              } : undefined
            })) || []
          }))
        };

        setData({
          pages: [pricingSaasResponse],
          currentPage: 0
        });
        setError(null);
      } else {
        setError({
          type: 'PARSE_ERROR',
          message: 'No pricing data received from API'
        });
        setData(null);
      }
    } catch (err) {
      console.error('Error fetching PricingSaaS data:', err);
      setError({
        type: 'UNKNOWN',
        message: err instanceof Error ? err.message : 'An unexpected error occurred'
      });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [slug, selectedPageIndex]);

  // Fetch versions on mount
  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  // Fetch data when slug or selectedVersion changes
  useEffect(() => {
    fetchData(selectedVersion);
  }, [slug, selectedVersion]);

  // Refetch function for manual refresh
  const refetch = useCallback(async () => {
    await fetchData(selectedVersion);
  }, [fetchData, selectedVersion]);

  // Handle version selection
  const handleSetSelectedVersion = useCallback((version: string | null) => {
    setSelectedVersion(version);
  }, []);

  return {
    data,
    loading,
    error,
    selectedPageIndex,
    setSelectedPageIndex,
    refetch,
    versions,
    selectedVersion,
    setSelectedVersion: handleSetSelectedVersion,
    versionsLoading
  };
};
